import React, { useState } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS } from './tokens';

export interface GridContext {
  stations: { name: string | null; operator: string | null; voltage_kv: number | null; county: string | null; attribute_confidence: string; geometry_confidence: string; source_url: string | null }[];
  circuits: { name: string | null; operator: string | null; voltage_kv: number | null; circuit_miles: number | null; operational_status: string; source_url: string | null }[];
  generation: { name: string | null; capacity_mw: number | null; county: string | null; operational_status: string }[];
  queue_by_county: { county: string; projects: number; mw: number; largest: string }[];
  reconciled_as_of: string | null;
  scope_note: string;
}

const label: React.CSSProperties = {
  fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5, letterSpacing: '0.09em',
  textTransform: 'uppercase', color: REPORT.tertiary, marginBottom: 3,
};

export const GridContextPanel: React.FC<{ grid: GridContext }> = ({ grid }) => {
  const [open, setOpen] = useState(false);
  return (
    <section style={{ border: `1px solid ${REPORT.rule}`, borderRadius: 4, padding: '8px 12px', marginBottom: 10 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        all: 'unset', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', width: '100%',
        fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: REPORT.secondary,
      }}>
        <span>Grid context — {grid.stations.length} verified stations · {grid.queue_by_county.reduce((s, q) => s + q.mw, 0).toLocaleString()} MW in queue</span>
        <span>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 11.5 }}>
          <div>
            <div style={label}>Named stations (existing)</div>
            {grid.stations.map(s => (
              <div key={s.name ?? ''} style={{ padding: '2px 0' }}>
                <strong>{s.name}</strong> · {s.operator || 'op unknown'} · {s.voltage_kv ? `${s.voltage_kv} kV` : '—'}
                <span style={{ color: REPORT.tertiary }}> ({s.attribute_confidence})</span>
              </div>
            ))}
            <div style={{ ...label, marginTop: 8 }}>Circuits</div>
            {grid.circuits.map(c => (
              <div key={c.name ?? ''} style={{ padding: '2px 0' }}>
                {c.name} · {c.voltage_kv} kV
                {c.operational_status !== 'existing' && <em style={{ color: REPORT.tertiary }}> — {c.operational_status}, not energized</em>}
              </div>
            ))}
          </div>
          <div>
            <div style={label}>Generation</div>
            {grid.generation.map(g => (
              <div key={g.name ?? ''} style={{ padding: '2px 0' }}>
                {g.name} · {g.capacity_mw} MW{g.operational_status !== 'existing' && <em style={{ color: REPORT.tertiary }}> — {g.operational_status}</em>}
              </div>
            ))}
            <div style={{ ...label, marginTop: 8 }}>Interconnection queue (≥50 MW)</div>
            {grid.queue_by_county.map(q => (
              <div key={q.county} style={{ padding: '2px 0' }}>
                {q.county}: {q.projects} projects, {q.mw.toLocaleString()} MW — largest {q.largest}
              </div>
            ))}
          </div>
          <div style={{ gridColumn: '1 / -1', color: REPORT.tertiary, fontSize: 10.5, borderTop: `1px solid ${REPORT.rule}`, paddingTop: 6 }}>
            Grid inventory reconciled against ERCOT bus registry, FERC Form 1 (Oncor), OSM, and HIFLD
            {grid.reconciled_as_of ? ` as of ${String(grid.reconciled_as_of).slice(0, 10)}` : ''}. {grid.scope_note} Capacity figures are nameplate — never interconnection headroom.
          </div>
        </div>
      )}
    </section>
  );
};
